import React, { useState, useEffect, useContext } from "react";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API_BASE_URL } from "../../environment";
import { all_routes } from "../router/all_routes";
import { AuthContext } from "../../context/authcontext";
import AuthCardLayout from "./AuthCardLayout";
import { getAuthApiError } from "./authApiError";

export const ForgotPassword: React.FC = () => {
  const MySwal = withReactContent(Swal);
  const navigate = useNavigate();
  const route = all_routes;
  const { auth } = useContext(AuthContext);

  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (auth) {
      navigate(route.dealsDashboard, { replace: true });
      return;
    }
  }, [auth, navigate, route.dealsDashboard]);

  const validateEmail = (email: string) =>
    /^[\w-.]+@[\w-]+\.[a-zA-Z]{2,}$/.test(email);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;

    if (!validateEmail(email)) {
      return MySwal.fire({
        title: "Invalid Email",
        text: "Please enter a valid email address.",
        icon: "error",
        confirmButtonText: "OK",
      });
    }

    setIsSubmitting(true);

    try {
      const sendRes = await axios.post(
        `${API_BASE_URL}/forgot-password/`,
        { email },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (sendRes.status === 200) {
        await MySwal.fire({
          title: "Check Your Inbox",
          text: "A password reset link has been sent to your email.",
          icon: "success",
          confirmButtonText: "Back to Login",
          allowOutsideClick: false,
          allowEscapeKey: false,
        });
        navigate(route.login, { replace: true });
        return;
      }

      setIsSubmitting(false);
      await MySwal.fire({
        title: "Oops!",
        text: "Couldn't send reset link. Please try again later.",
        icon: "error",
        confirmButtonText: "OK",
      });
    } catch (err: unknown) {
      setIsSubmitting(false);
      const { title, message } = getAuthApiError(
        err,
        "Error",
        "Failed to send reset link. Please try again."
      );
      MySwal.fire({
        title,
        text: message,
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  };

  return (
    <AuthCardLayout
      icon="ti-lock-question"
      title="Forgot Password?"
      subtitle="Enter the email linked to your account and we'll send you a link to reset your password."
    >
      <form onSubmit={handleSubmit} noValidate>
        <div className="mb-4">
          <label className="form-label" htmlFor="forgot-password-email">
            Email Address
          </label>
          <div className="position-relative">
            <span className="input-icon-addon">
              <i className="ti ti-mail" />
            </span>
            <input
              id="forgot-password-email"
              type="email"
              className="form-control"
              value={email ?? ""}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="name@company.com"
              autoComplete="email"
              disabled={isSubmitting}
              required
            />
          </div>
        </div>

        <button
          type="submit"
          className="btn btn-primary w-100 d-inline-flex align-items-center justify-content-center"
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <span
                className="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              />
              Sending reset link...
            </>
          ) : (
            <>
              <i className="ti ti-send me-2" />
              Send Reset Link
            </>
          )}
        </button>
      </form>
    </AuthCardLayout>
  );
};

export default ForgotPassword;
